import {
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useAppSelector } from "hooks/use-app-select";
import { useState } from "react";
import { selectAuthenticated } from "redux/authSlice";
import history from "utils/history";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import { forgotPassword } from "screens/Auth/AuthService";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import urls from "global/constants/UrlConstants";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { CustomInput } from "global/components";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { LoginFields } from "models/interfaces";
import { useAuthRouteRedirection } from "layouts/Config";
import viewpaths from "global/constants/ViewPathConstants";
import { mobile, signer, signerAccentBar } from "theme/colors";
import { authRef, refFont } from "./AuthReferenceStyles";

const ForgotPassword = (props: any) => {
  useTitle(title.FORGOT_PASSWORD);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const [processing, setProcessing] = useState<boolean>(false);
  const [credentials, setCredentials] = useState({
    email: {
      value: "",
      error: "",
    },
  } as LoginFields);

  const handleChange = (event: any) => {
    setCredentials({
      ...credentials,
      [event.target.name]: {
        value: event.target.value,
        error: "",
      },
    });
  };

  const handleValidation = () => {
    let errors = credentials;
    let isValid = true;
    const email = credentials.email.value;
    if (!email) {
      errors.email.error = "Please enter email";
      isValid = false;
    }
    setCredentials({ ...errors });
    return isValid;
  };

  const handleForgotPassword = async () => {
    try {
      if (handleValidation()) {
        setProcessing(true);
        let captchaToken: any;
        if (urls.PROD) {
          captchaToken = await executeRecaptcha!();
        }
        await forgotPassword(credentials.email.value, captchaToken);
        openSuccessNotification(
          "We have sent you an email with your password reset link.",
        );
        history.push(viewpaths.landingViewPath);
        setProcessing(false);
      }
    } catch (error: any) {
      setProcessing(false);
      handleErrorMessage(error);
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleForgotPassword();
    }
  };

  const loginScreen = () => {
    return (
      <Box>
        {/* Brand accent bar across the top of the canvas, per reference 3d. */}
        <Box
          aria-hidden="true"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: signerAccentBar,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        />
        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 460,
            mx: "auto",
            borderRadius: "16px",
            border: `1px solid ${authRef.cardBorder}`,
            boxShadow: authRef.cardShadow,
            padding: { xs: "28px 24px 24px", sm: "40px 44px 36px" },
            boxSizing: "border-box",
          }}
        >
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                component="img"
                src={
                  require("assets/images/side_nav_inkfree_logo.svg").default ||
                  require("assets/images/side_nav_inkfree_logo.svg")
                }
                alt="Inkfree e-Sign"
                sx={{ width: 106, height: "auto", display: "block" }}
              />
              <Typography
                component="h1"
                sx={{
                  color: mobile.base[500],
                  fontSize: refFont.cardHeading,
                  fontWeight: 700,
                  textAlign: "center",
                  mt: 3,
                }}
              >
                Forgot your password?
              </Typography>
              <Typography
                sx={{
                  color: authRef.subText,
                  fontSize: refFont.cardBody,
                  lineHeight: 1.6,
                  textAlign: "center",
                  mt: 1,
                }}
              >
                Happens to the best of us. Tell us your email and we'll send you
                reset instructions.
              </Typography>
            </Box>
            <Stack
              spacing={2}
              sx={{
                mt: 3.25,
                // Field chrome per reference 3d: 46px tall, 8px radius, pale
                // fill, 14px inset. `&&` outranks CustomInput's !important.
                "&& .MuiOutlinedInput-root": {
                  minHeight: "46px",
                  height: "46px",
                  borderRadius: "8px",
                  padding: "0 14px !important",
                  backgroundColor: authRef.inputBg,
                  fontSize: refFont.body,
                  fontWeight: 500,
                  color: mobile.base[500],
                  "& fieldset": { borderColor: authRef.inputBorder },
                  "&:hover fieldset": { borderColor: authRef.inputBorderHover },
                },
                "&& .MuiInputBase-input": { padding: "0 !important" },
                "&& .MuiInputBase-input::placeholder": {
                  color: authRef.metaText,
                  opacity: 1,
                },
                "&& .MuiInputLabel-root": {
                  fontSize: refFont.label,
                  fontWeight: 600,
                  color: mobile.base[500],
                  marginLeft: 0,
                  "& .MuiFormLabel-asterisk": { color: signer.orange.main },
                },
              }}
            >
              <Box>
                <CustomInput
                  required
                  label="Business email"
                  autoFocus
                  placeHolder="you@company.com"
                  name="email"
                  onChange={handleChange}
                  type="email"
                  value={credentials.email.value}
                  disabled={processing}
                  propsToInputElement={{
                    maxLength: 50,
                    "aria-required": true,
                    "aria-invalid": !!credentials.email.error,
                  }}
                  error={
                    !isTruthy(credentials.email.value) &&
                    credentials.email.error
                  }
                  onKeyPress={handleKeypress}
                  helperText={
                    !isTruthy(credentials.email.value)
                      ? credentials.email.error
                      : ""
                  }
                />
              </Box>
            </Stack>
            <Button
              id="Send password reset instructions"
              fullWidth
              size="large"
              sx={{
                mt: 2.75,
                height: "48px",
                borderRadius: "8px",
                fontWeight: 700,
                textTransform: "none",
                fontSize: refFont.cardButton,
                letterSpacing: 0,
                outline: "none",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: "2px",
                },
              }}
              type="submit"
              variant="contained"
              onClick={handleForgotPassword}
              disabled={processing}
            >
              Send reset link
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2.75,
                cursor: "pointer",
              }}
            >
              <Link
                underline="hover"
                onClick={() => history.push(viewpaths.loginViewPath)}
                sx={{
                  color: signer.orange.main,
                  fontSize: refFont.label,
                  fontWeight: 700,
                  textDecoration: "none",
                  outline: "none",
                  "&:hover": { textDecoration: "underline" },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: signer.orange.main,
                    outlineOffset: "2px",
                  },
                }}
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    history.push(viewpaths.loginViewPath);
                }}
              >
                &larr; Back to log in
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (isLoggedIn) {
    history.push(authRoute);
    return null;
  } else {
    return <AuthLayout>{loginScreen()}</AuthLayout>;
  }
};

export default ForgotPassword;
